import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Trophy, Target, Star, Mail, Phone } from 'lucide-react';

export default function Coaches() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      <div className="container mx-auto py-16 px-4">
        {/* Header Section */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-secondary text-slate-900 font-black">
            FOR COACHES
          </Badge>
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-6">
            Find Your Next
            <span className="block text-secondary">Star Athlete</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Connect with talented student-athletes and streamline your recruiting process with powerful tools designed for college coaches
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          <div className="text-center">
            <div className="text-4xl font-black text-secondary mb-2">50K+</div>
            <div className="text-gray-600">Student Athletes</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-black text-secondary mb-2">500+</div>
            <div className="text-gray-600">College Programs</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-black text-secondary mb-2">95%</div>
            <div className="text-gray-600">Satisfaction Rate</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-black text-secondary mb-2">24/7</div>
            <div className="text-gray-600">Support</div>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <Target className="w-12 h-12 text-secondary mb-4" />
              <CardTitle>Advanced Search</CardTitle>
              <CardDescription>
                Find athletes by sport, position, location, GPA, and more with our powerful search filters
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <Users className="w-12 h-12 text-secondary mb-4" />
              <CardTitle>Prospect Management</CardTitle>
              <CardDescription>
                Organize and track your recruiting prospects with custom lists and detailed notes
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <Trophy className="w-12 h-12 text-secondary mb-4" />
              <CardTitle>Performance Analytics</CardTitle>
              <CardDescription>
                Access detailed athlete statistics, game footage, and performance metrics
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Coach Testimonial */}
        <div className="bg-slate-900 text-white rounded-2xl p-8 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-6 h-6 text-secondary fill-current" />
              ))}
            </div>
            <blockquote className="text-xl md:text-2xl font-medium mb-6">
              "RecruitLook has completely transformed our recruiting process. We've identified and signed more quality student-athletes than ever before."
            </blockquote>
            <cite className="font-bold">
              Coach Sarah Williams<br />
              <span className="text-secondary font-normal">University of California Basketball</span>
            </cite>
          </div>
        </div>

        {/* Coaching Tools */}
        <div className="mb-16">
          <h2 className="text-3xl font-black text-center mb-12">Coaching Tools & Resources</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Recruiting Dashboard</CardTitle>
                <CardDescription>
                  Centralized hub for all your recruiting activities and prospect management
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li>• Custom prospect lists</li>
                  <li>• Communication tracking</li>
                  <li>• Recruiting calendar</li>
                  <li>• Team needs planning</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Communication Hub</CardTitle>
                <CardDescription>
                  Streamlined communication tools to connect with prospects and their families
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li>• Email templates</li>
                  <li>• Message scheduling</li>
                  <li>• Compliance tracking</li>
                  <li>• Follow-up reminders</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl font-black mb-6">Ready to Find Your Next Recruit?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join hundreds of college coaches who trust RecruitLook to build championship teams
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-slate-900 font-black px-8 py-4">
              START FREE TRIAL
            </Button>
            <Button variant="outline" size="lg" className="px-8 py-4 font-black">
              <Phone className="w-4 h-4 mr-2" />
              SCHEDULE DEMO
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}